import React, { useState, useEffect } from 'react';
import {
  Plus,
  Filter,
  Search,
  List,
  Grid,
  Mail,
  Archive,
  Trash2,
  Users,
  Download,
  Upload,
  ChevronDown,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import EmptyState from '../components/EmptyState';
import ResourceModal from '../components/ResourceModal';
import ResourceDetailsModal from '../components/ResourceDetailsModal';
import { getResources, Resource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import PermissionGuard from '../components/PermissionGuard';
import { supabase } from '../lib/supabase';
// Removed usePendingTimeOffCount hook - now handled directly in LeftNavigation

interface TimeOffRequest {
  id: string;
  start_date: string;
  end_date: string;
  request_type: string;
  is_partial_day: boolean;
  start_time?: string;
  end_time?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  approved_by?: string;
  approved_at?: string;
  reason_for_denial?: string;
  resource_id: string;
  business_id: string;
  resource?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url?: string;
  };
}

const statusOptions = [
  { label: 'All Resources', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const timeOffStatusOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Denied', value: 'denied' },
  { label: 'All Requests', value: 'all' },
];

export default function Resources() {
  const { selectedBusiness, role } = useBusiness();
  const [viewType, setViewType] = useState('table');
  const [selectedResources, setSelectedResources] = useState(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[1]); // Default to 'Active'
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);
  const [isResourceDetailsModalOpen, setIsResourceDetailsModalOpen] =
    useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null
  );
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [isLoadingTimeOff, setIsLoadingTimeOff] = useState(false);
  const [timeOffDisplayLimit, setTimeOffDisplayLimit] = useState(3);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [timeOffStatusFilter, setTimeOffStatusFilter] = useState('pending');
  const [isTimeOffStatusOpen, setIsTimeOffStatusOpen] = useState(false);

  // Navigation badge is now handled directly in LeftNavigation component

  const loadResources = async () => {
    if (!selectedBusiness?.id) {
      console.log('No business selected, skipping resource fetch');
      setLoading(false);
      return;
    }

    try {
      console.log('Loading resources for business:', selectedBusiness.id);
      setLoading(true);
      const data = await getResources(selectedBusiness.id);
      console.log('Resources loaded:', data);
      setResources(data);

      // Update selected resource if it exists
      if (selectedResource) {
        const updatedResource = data.find((r) => r.id === selectedResource.id);
        if (updatedResource) {
          setSelectedResource(updatedResource);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(
      'Resources component mounted, selectedBusiness:',
      selectedBusiness
    );
    loadResources();
    loadTimeOffRequests();

    // Set up real-time subscription for time off requests
    if (!selectedBusiness?.id || !supabase) return;
    
    const timeOffSubscription = supabase
      .channel('resources_time_off_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_off_requests',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          // Reload time off requests when any change occurs
          loadTimeOffRequests();
        }
      )
      .subscribe();

    return () => {
      if (supabase) {
        supabase.removeChannel(timeOffSubscription);
      }
    };
  }, [selectedBusiness?.id]);

  const loadTimeOffRequests = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    setIsLoadingTimeOff(true);
    try {
      console.log('Loading time off requests for business:', selectedBusiness.id);
      
      // Try the join query first
      let { data: requests, error } = await supabase
        .from('time_off_requests')
        .select(`
          *,
          resource:resources(
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .order('requested_at', { ascending: false });

      if (error) {
        console.error('Join query failed, trying alternative approach:', error);
        
        // Fallback: Load requests and resources separately
        const { data: basicRequests, error: basicError } = await supabase
          .from('time_off_requests')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('requested_at', { ascending: false });

        if (basicError) {
          console.error('Error fetching basic time off requests:', basicError);
          return;
        }

        console.log('Basic requests loaded:', basicRequests);

        // Now fetch resource data for each request
        if (basicRequests && basicRequests.length > 0) {
          const resourceIds = [...new Set(basicRequests.map(r => r.resource_id))];
          const { data: resourcesData, error: resourcesError } = await supabase
            .from('resources')
            .select('id, first_name, last_name, email, avatar_url')
            .in('id', resourceIds);

          if (resourcesError) {
            console.error('Error fetching resources:', resourcesError);
            // Still set the requests without resource data
            setTimeOffRequests(basicRequests || []);
            return;
          }

          // Combine the data
          const requestsWithResources = basicRequests.map(request => ({
            ...request,
            resource: resourcesData?.find(r => r.id === request.resource_id)
          }));

          requests = requestsWithResources;
        } else {
          requests = basicRequests || [];
        }
      }

      console.log('Time off requests loaded:', requests);
      setTimeOffRequests(requests || []);
    } catch (error) {
      console.error('Error loading time off requests:', error);
    } finally {
      setIsLoadingTimeOff(false);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('time_off_requests')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          // Note: approved_by would need to be set to the current user's resource ID
        })
        .eq('id', requestId);

      if (error) {
        console.error('Error approving request:', error);
        return;
      }

      // Refresh the requests list
      loadTimeOffRequests();
      // The navigation badge will update automatically via real-time subscription
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleDenyRequest = async (requestId: string, reason?: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('time_off_requests')
        .update({
          status: 'denied',
          approved_at: new Date().toISOString(),
          reason_for_denial: reason,
          // Note: approved_by would need to be set to the current user's resource ID
        })
        .eq('id', requestId);

      if (error) {
        console.error('Error denying request:', error);
        return;
      }

      // Refresh the requests list
      loadTimeOffRequests();
      // The navigation badge will update automatically via real-time subscription
    } catch (error) {
      console.error('Error denying request:', error);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('time_off_requests')
        .delete()
        .eq('id', requestId);

      if (error) {
        console.error('Error canceling request:', error);
        return;
      }

      // Refresh the requests list
      loadTimeOffRequests();
      // The navigation badge will update automatically via real-time subscription
    } catch (error) {
      console.error('Error canceling request:', error);
    }
  };

  const filteredResources = resources.filter((resource) => {
    // Apply status filter
    if (
      selectedStatus.value !== 'all' &&
      resource.status !== selectedStatus.value
    ) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      resource.first_name.toLowerCase().includes(searchLower) ||
      resource.last_name.toLowerCase().includes(searchLower) ||
      resource.email.toLowerCase().includes(searchLower) ||
      resource.role.toLowerCase().includes(searchLower)
    );
  });

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedResources(new Set(resources.map((_, index) => index)));
    } else {
      setSelectedResources(new Set());
    }
  };

  const toggleResource = (index: number) => {
    const newSelected = new Set(selectedResources);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedResources(newSelected);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'inactive':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleResourceClick = (resource: Resource) => {
    setSelectedResource(resource);
    setIsResourceDetailsModalOpen(true);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}>
        <div className="text-center bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-8" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Resources
          </h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="resources.pageAccess">
      <div 
        className="flex flex-col h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" 
        data-resources-page
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          margin: 0,
          padding: 0
        }}>

        {/* Filters and Search Section */}
        <div className="bg-white/90 backdrop-blur-xl border-b border-gray-200/30 px-4 md:px-8 py-4 sticky top-0 z-30" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between">
            {/* Left side - Filter and Search */}
            <div className="flex flex-row items-center space-x-4 w-full md:w-auto">
              {/* Status Filter */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setIsStatusOpen(!isStatusOpen)}
                  className="inline-flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation min-w-[140px]"
                  style={{ minHeight: '44px' }}
                >
                  <span className="truncate">{selectedStatus.label}</span>
                  <ChevronDown
                    className={`ml-2 h-5 w-5 flex-shrink-0 transition-transform duration-300 ${
                      isStatusOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isStatusOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl border border-gray-200/50 z-50 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                  }}>
                    <div className="py-2" role="menu" aria-orientation="vertical">
                      {statusOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSelectedStatus(option);
                            setIsStatusOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                            selectedStatus.value === option.value
                              ? 'bg-blue-50 text-blue-900 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          role="menuitem"
                          style={{ minHeight: '44px' }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Bar */}
              <div className="relative flex-1 md:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search team members"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

  {/* Right side - Time Off Requests and Add Team Member Buttons */}
  <div className="flex flex-row space-x-3 md:space-y-0 md:flex-row md:items-center md:space-x-3 w-full md:w-auto">
    {/* Time Off Requests Button */}
    <div className="flex-1 md:w-auto md:flex-none">
      <button
        onClick={() => setIsTimeOffModalOpen(true)}
        className="w-full md:w-auto inline-flex items-center justify-center px-4 md:px-6 py-3 text-sm md:text-base font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation relative"
        style={{ minHeight: '44px' }}
      >
        <span className="hidden sm:inline">Time Off Requests</span>
        <span className="sm:hidden">Time Off</span>
        {/* Notification Badge for Pending Requests */}
        {timeOffRequests.filter(request => request.status === 'pending').length > 0 && (
          <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
            {timeOffRequests.filter(request => request.status === 'pending').length}
          </span>
        )}
      </button>
    </div>

    {/* Add Team Member Button */}
    {role === 'admin' && (
      <div className="flex-1 md:w-auto md:flex-none">
        <button
          onClick={() => setIsNewResourceModalOpen(true)}
          className="w-full md:w-auto inline-flex items-center justify-center px-4 md:px-6 py-3 text-sm md:text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
          style={{ minHeight: '44px' }}
        >
          <Plus className="h-4 md:h-5 w-4 md:w-5 mr-1 md:mr-2" />
          <span className="hidden sm:inline">Add Team Member</span>
          <span className="sm:hidden">Add Member</span>
        </button>
      </div>
    )}
  </div>
          </div>
        </div>

        {/* Main Content Container */}
        <div className="flex-1 px-4 md:px-8 py-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-blue-600/30"></div>
                <p className="mt-4 text-base text-gray-600 font-medium">Loading team members...</p>
              </div>
            </div>
          ) : resources.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<Users className="w-8 h-8 text-gray-400" />}
                title="No team members yet"
                description="Start adding team members to manage your workforce effectively."
                actionLabel="Add your first team member"
                onAction={() => setIsNewResourceModalOpen(true)}
              />
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<Users className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching team members' : 'No team members found'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms or filter criteria'
                    : 'No team members match the current filter criteria.'
                }
                actionLabel={searchQuery ? 'Clear search' : 'Reset filters'}
                onAction={() => {
                  if (searchQuery) {
                    setSearchQuery('');
                  } else {
                    setSelectedStatus(statusOptions[0]);
                  }
                }}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredResources.map((resource, index) => (
                <div
                  key={resource.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex"
                  onClick={() => handleResourceClick(resource)}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  {/* Status Bar - Left Edge */}
                  <div className={`w-1 rounded-l-xl ${
                    resource.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  
                  <div className="p-6 flex-1">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white overflow-hidden">
                          {resource.avatar_url ? (
                            <img 
                              src={resource.avatar_url} 
                              alt={`${resource.first_name} ${resource.last_name}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            `${resource.first_name[0]}${resource.last_name[0]}`
                          )}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {resource.first_name} {resource.last_name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">{resource.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          resource.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {resource.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="mb-4">
                      <div className="text-sm text-gray-700">
                        <span className="font-medium">Role:</span>
                        <span className="ml-2">{resource.permission_role?.name || 'No permissions assigned'}</span>
                      </div>
                    </div>

                    {/* Last Active and Selection */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">
                          Last Active: {resource.last_active 
                            ? new Date(resource.last_active).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'No activity yet'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20 transition-all duration-200"
                          checked={selectedResources.has(index)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleResource(index);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


        <ResourceModal
          isOpen={isNewResourceModalOpen}
          onClose={() => setIsNewResourceModalOpen(false)}
          onSuccess={() => {
            setIsNewResourceModalOpen(false);
            loadResources();
          }}
          businessId={selectedBusiness?.id}
        />

        <ResourceDetailsModal
          isOpen={isResourceDetailsModalOpen}
          onClose={() => {
            setIsResourceDetailsModalOpen(false);
            setSelectedResource(null);
          }}
          resource={selectedResource}
          onUpdate={loadResources}
        />

        {/* Time Off Requests Modal */}
        {isTimeOffModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsTimeOffModalOpen(false)} />
              
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                {/* Modal Header */}
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-xl">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Time Off Requests</h2>
                        <p className="text-sm text-gray-600">Manage team member time off requests</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Notification Badge for Pending Requests */}
                      {timeOffRequests.filter(request => request.status === 'pending').length > 0 && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                          {timeOffRequests.filter(request => request.status === 'pending').length} New Request{timeOffRequests.filter(request => request.status === 'pending').length !== 1 ? 's' : ''}
                        </span>
                      )}
                      <button
                        onClick={() => setIsTimeOffModalOpen(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div className="mb-4">
                    <div className="relative w-48">
                      <button
                        onClick={() => setIsTimeOffStatusOpen(!isTimeOffStatusOpen)}
                        className="w-full inline-flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                      >
                        <span>{timeOffStatusOptions.find(option => option.value === timeOffStatusFilter)?.label}</span>
                        <ChevronDown
                          className={`ml-2 h-4 w-4 transition-transform duration-200 ${
                            isTimeOffStatusOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {isTimeOffStatusOpen && (
                        <div className="absolute left-0 mt-2 w-full rounded-lg shadow-lg bg-white border border-gray-200 z-50">
                          <div className="py-1" role="menu">
                            {timeOffStatusOptions.map((option) => (
                              <button
                                key={option.value}
                                onClick={() => {
                                  setTimeOffStatusFilter(option.value);
                                  setIsTimeOffStatusOpen(false);
                                  setTimeOffDisplayLimit(3); // Reset pagination when filter changes
                                }}
                                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                  timeOffStatusFilter === option.value
                                    ? 'bg-blue-50 text-blue-900 font-medium'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                role="menuitem"
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Modal Content */}
                  <div className="max-h-96 overflow-y-auto">
                    {isLoadingTimeOff ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                        <span className="ml-3 text-gray-600">Loading time off requests...</span>
                      </div>
                    ) : timeOffRequests.length > 0 ? (
                      <>
                        <div className="space-y-3">
                          {timeOffRequests
                            .filter(request => request.resource)
                            .filter(request => timeOffStatusFilter === 'all' || request.status === timeOffStatusFilter)
                            .slice(0, timeOffDisplayLimit)
                            .map((request) => {
                              const statusConfig = {
                                pending: { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
                                approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
                                denied: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' }
                              };
                              
                              const config = statusConfig[request.status];
                              const StatusIcon = config.icon;
                              
                              const formatDate = (dateStr: string) => {
                                try {
                                  // Parse as local date to avoid timezone shifting
                                  const [year, month, day] = dateStr.split('-').map(Number);
                                  const localDate = new Date(year, month - 1, day); // month is 0-indexed
                                  return format(localDate, 'MMM dd, yyyy');
                                } catch {
                                  return dateStr;
                                }
                              };

                              const getTypeLabel = (type: string) => {
                                const typeMap = {
                                  vacation: 'Vacation',
                                  sick: 'Sick Leave',
                                  personal: 'Personal Day',
                                  family: 'Family Emergency',
                                  bereavement: 'Bereavement',
                                  other: 'Other'
                                };
                                return typeMap[type as keyof typeof typeMap] || type;
                              };

                              // Skip if no resource data
                              if (!request.resource) {
                                console.warn('Request missing resource data:', request.id);
                                return null;
                              }

                              return (
                                <div 
                                  key={request.id} 
                                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 ease-out"
                                >
                                  <div className="p-4">
                                    <div className="flex items-start space-x-3">
                                      {/* Employee Avatar */}
                                      <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-sm font-bold text-white shadow-sm overflow-hidden">
                                          {request.resource.avatar_url ? (
                                            <img 
                                              src={request.resource.avatar_url} 
                                              alt={`${request.resource.first_name} ${request.resource.last_name}`}
                                              className="h-full w-full object-cover"
                                            />
                                          ) : (
                                            `${request.resource.first_name[0]}${request.resource.last_name[0]}`
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Request Details */}
                                      <div className="flex-1 min-w-0">
                                        {/* Header Row */}
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-semibold text-gray-900 truncate">
                                              {request.resource.first_name} {request.resource.last_name}
                                            </h3>
                                            <p className="text-xs text-gray-600 font-medium truncate">{getTypeLabel(request.request_type)}</p>
                                          </div>
                                          
                                          {/* Status Badge */}
                                          <div className="flex-shrink-0 ml-3">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-lg shadow-sm ${config.color} ${config.bg}`}>
                                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {/* Details Row */}
                                        <div className="space-y-1">
                                          <p className="text-xs text-gray-600">
                                            <span className="font-medium">Dates:</span> {formatDate(request.start_date)}{request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                                            {request.is_partial_day && (
                                              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                                {request.start_time} - {request.end_time}
                                              </span>
                                            )}
                                          </p>
                                          {request.notes && (
                                            <p className="text-xs text-gray-600">
                                              <span className="font-medium">Note:</span> {request.notes.length > 50 ? `${request.notes.substring(0, 50)}...` : request.notes}
                                            </p>
                                          )}
                                          <p className="text-xs text-gray-600">
                                            <span className="font-medium">Requested:</span> {format(new Date(request.requested_at), 'MMM dd, yyyy')}
                                          </p>
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="flex space-x-2 mt-3">
                                          {request.status === 'pending' && (
                                            <>
                                              <button
                                                onClick={() => handleApproveRequest(request.id)}
                                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200"
                                              >
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Approve
                                              </button>
                                              <button
                                                onClick={() => handleDenyRequest(request.id)}
                                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200"
                                              >
                                                <XCircle className="h-3 w-3 mr-1" />
                                                Deny
                                              </button>
                                            </>
                                          )}
                                          {/* Cancel button for all statuses */}
                                          <button
                                            onClick={() => handleCancelRequest(request.id)}
                                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                                          >
                                            <X className="h-3 w-3 mr-1" />
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        
                        {/* View More Button */}
                        {(() => {
                          const filteredRequests = timeOffRequests
                            .filter(request => request.resource)
                            .filter(request => timeOffStatusFilter === 'all' || request.status === timeOffStatusFilter);
                          return filteredRequests.length > timeOffDisplayLimit && (
                            <div className="mt-4 text-center">
                              <button
                                onClick={() => setTimeOffDisplayLimit(prev => prev + 3)}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                              >
                                View More ({filteredRequests.length - timeOffDisplayLimit} remaining)
                              </button>
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-base font-medium text-gray-900 mb-2">
                          {(() => {
                            const filteredRequests = timeOffRequests
                              .filter(request => request.resource)
                              .filter(request => timeOffStatusFilter === 'all' || request.status === timeOffStatusFilter);
                            
                            if (filteredRequests.length === 0 && timeOffRequests.length > 0) {
                              return `No ${timeOffStatusFilter === 'all' ? '' : timeOffStatusFilter + ' '}requests found`;
                            }
                            return 'No Time Off Requests';
                          })()}
                        </h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto">
                          {(() => {
                            const filteredRequests = timeOffRequests
                              .filter(request => request.resource)
                              .filter(request => timeOffStatusFilter === 'all' || request.status === timeOffStatusFilter);
                            
                            if (filteredRequests.length === 0 && timeOffRequests.length > 0) {
                              return `No ${timeOffStatusFilter === 'all' ? '' : timeOffStatusFilter + ' '}requests match your current filter. Try selecting a different status.`;
                            }
                            
                            if (timeOffRequests.length > 0) {
                              return `Found ${timeOffRequests.length} request(s) but unable to load resource information.`;
                            }
                            
                            return 'No team members have submitted time off requests yet.';
                          })()}
                        </p>
                        {timeOffRequests.length > 0 && (
                          <div className="mt-4">
                            <button 
                              onClick={loadTimeOffRequests}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Try reloading
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
}